/**
 * دروازه‌ی رمز نسخه‌ی مشتری.
 *
 * تنها یک رمز مشترک. بدون کاربر، نقش، ثبت‌نام یا بازیابی رمز — این الزام محصول
 * است، نه سادگی موقت.
 *
 * throttle و شمارش تلاش ناموفق **سمت سرور** انجام می‌شود. اینجا هیچ شمارنده‌ای
 * نیست: شمارنده‌ی سمت کلاینت با یک بار refresh صفر می‌شود و امنیتی به‌حساب
 * نمی‌آید.
 *
 * چیدمان دو ستونی است: ستون تیره‌ی معرفی و ستون فرم. نخستین صفحه‌ای است که
 * مشتری می‌بیند و باید بگوید این ابزار چه می‌کند، نه فقط یک جعبه‌ی رمز باشد.
 */

import { useMutation } from '@tanstack/react-query'
import { KeyRound, Lock, ScanSearch, ShieldCheck, Sparkles } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Banner } from '@/components/ui/primitives'
import { ApiError } from '@/lib/api/client'
import { createSession } from '@/lib/api/endpoints'
import { useMeta } from '@/lib/meta-context'

const HIGHLIGHTS = [
  {
    icon: ScanSearch,
    title: 'برآورد بر پایه‌ی سابقه',
    detail: 'هر پاسخ به تصمیم‌های واقعی همین سازمان تکیه دارد، نه حدس عمومی.',
  },
  {
    icon: ShieldCheck,
    title: 'داده روی همین سرور',
    detail: 'لاگ مشاوره‌ها محلی می‌ماند و به سرویس سومی نمی‌رود.',
  },
] as const

export const AccessGate = () => {
  const { meta, signIn } = useMeta()
  const [code, setCode] = useState('')

  const login = useMutation({
    mutationFn: (accessCode: string) => createSession(accessCode),
    onSuccess: (session) => {
      signIn(session.token)
    },
  })

  const error = login.error
  const message = error instanceof ApiError ? error.message : null
  // قفل شدن به‌خاطر تلاش زیاد، پیام و رفتار متفاوتی دارد: دکمه غیرفعال
  // می‌شود تا کاربر بی‌فایده تلاش نکند.
  const isLocked = error instanceof ApiError && error.code === 'AccessLocked'

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.05fr_1fr]">
      {/* ---------------- ستون معرفی ---------------- */}
      <section className="relative hidden overflow-hidden bg-[linear-gradient(160deg,var(--t-rail-from),var(--t-rail-to))] p-10 lg:flex lg:flex-col lg:justify-between">
        {/* دو هاله‌ی بزرگ و یک شبکه‌ی نقطه‌چین: عمق، بدون تصویر و بدون بافت پرنویز. */}
        <span
          className="pointer-events-none absolute -top-40 -end-24 size-[34rem] rounded-full bg-accent/25 blur-3xl"
          aria-hidden
        />
        <span
          className="pointer-events-none absolute -bottom-48 -start-32 size-[30rem] rounded-full bg-accent-two/20 blur-3xl"
          aria-hidden
        />
        <span
          className="dot-grid pointer-events-none absolute inset-0 opacity-[0.07]"
          aria-hidden
        />

        <div className="relative flex items-center gap-3">
          <span
            className="grid size-11 place-items-center rounded-md bg-linear-100 from-accent to-accent-two text-white shadow-glow ring-1 ring-inset ring-white/20"
            aria-hidden
          >
            <Sparkles className="size-5.5" />
          </span>
          <p className="text-sm font-bold text-white">{meta.profile.app_title}</p>
        </div>

        <div className="relative max-w-md">
          <h2 className="text-3xl leading-[1.35] font-bold text-white">
            تصمیم تازه را با
            <span className="mx-2 bg-linear-100 from-accent-three to-accent-two bg-clip-text text-transparent">
              سابقه‌ی خودتان
            </span>
            بسنجید.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-rail-ink">
            وضعیت را آزاد و به فارسی توصیف کنید. دستیار تصمیم‌های مشابه گذشته را پیدا می‌کند و
            یک برآورد ساختاریافته با شرط‌ها، ریسک‌ها و اطمینان می‌دهد.
          </p>

          <ul className="mt-8 space-y-4">
            {HIGHLIGHTS.map((item) => (
              <li key={item.title} className="flex gap-3.5">
                <span
                  className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-md bg-white/10 text-accent ring-1 ring-inset ring-white/10"
                  aria-hidden
                >
                  <item.icon className="size-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">{item.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-rail-ink-faint">
                    {item.detail}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-[11px] leading-relaxed text-rail-ink-faint">
          {meta.profile.disclaimer}
        </p>
      </section>

      {/* ---------------- ستون فرم ---------------- */}
      <section className="flex items-center justify-center p-6 sm:p-10">
        <div className="animate-rise w-full max-w-sm">
          <div className="mb-7 text-center lg:text-start">
            {/* در موبایل ستون معرفی پنهان است، پس نشان برند اینجا هم می‌آید. */}
            <span
              className="mb-4 inline-grid size-11 place-items-center rounded-md bg-linear-100 from-accent to-accent-two text-white shadow-glow ring-1 ring-inset ring-white/20 lg:hidden"
              aria-hidden
            >
              <Sparkles className="size-5.5" />
            </span>
            <h1 className="text-2xl font-bold tracking-tight text-ink">ورود به دستیار</h1>
            <p className="mt-2 text-sm leading-relaxed text-ink-muted">
              رمز دسترسی را وارد کنید. رمز را مدیر همین نسخه به شما داده است.
            </p>
          </div>

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault()
              if (code.trim() !== '') {
                login.mutate(code)
              }
            }}
          >
            <div className="space-y-2">
              <label htmlFor="access-code" className="text-xs font-semibold text-ink">
                رمز دسترسی
              </label>
              <div className="relative">
                <span
                  className="pointer-events-none absolute inset-y-0 start-0 grid w-11 place-items-center text-ink-faint"
                  aria-hidden
                >
                  <Lock className="size-4" />
                </span>
                <input
                  id="access-code"
                  type="password"
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value)
                  }}
                  // مرورگر نباید رمز مشترک سازمانی را در فرم‌های دیگر پیشنهاد دهد.
                  autoComplete="current-password"
                  autoFocus
                  className="h-12 w-full rounded-md border border-line-strong bg-surface-raised ps-11 pe-3.5 text-sm text-ink shadow-raised transition-[border-color,box-shadow] placeholder:text-ink-faint focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none"
                  placeholder="••••••••"
                  aria-invalid={message !== null}
                  {...(message === null ? {} : { 'aria-describedby': 'access-error' })}
                />
              </div>
            </div>

            {message !== null && (
              <div id="access-error">
                <Banner tone={isLocked ? 'warning' : 'danger'}>{message}</Banner>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              icon={<KeyRound className="size-4" aria-hidden />}
              loading={login.isPending}
              disabled={code.trim() === '' || isLocked}
            >
              ورود
            </Button>
          </form>

          <p className="mt-6 text-center text-[11px] leading-relaxed text-ink-faint lg:hidden">
            {meta.profile.disclaimer}
          </p>
        </div>
      </section>
    </div>
  )
}
